/**
 * RequestToBuyModal Component
 * Modal for requesting to buy a property
 * Implements S2.2 flow from feedback.md
 */

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "../../contexts/ToastContext";
import { useCreatePropertyRequestMutation } from "../../store/api/propertyRequestsApi";
import type { Property } from "../../types";
import Icon from "../AppIcon";
import Button from "../ui/Button";
import Input from "../ui/Input";
import Select, { type SelectValue } from "../ui/Select";

interface RequestToBuyModalProps {
	property: Property;
	isOpen: boolean;
	onClose: () => void;
}

const RequestToBuyModal: React.FC<RequestToBuyModalProps> = ({
	property,
	isOpen,
	onClose,
}) => {
	const navigate = useNavigate();
	const { success, error: showError } = useToast();
	const [createPropertyRequest, { isLoading }] =
		useCreatePropertyRequestMutation();
	const [currentStep, setCurrentStep] = useState<"basic" | "career" | "offer">(
		"basic",
	);

	// Basic Info
	const [basicInfo, setBasicInfo] = useState({
		firstName: "",
		lastName: "",
		email: "",
		phone: "",
	});

	// Career Info
	const [careerInfo, setCareerInfo] = useState({
		employmentStatus: "" as
			| "employed"
			| "self-employed"
			| "student"
			| "unemployed"
			| "",
		employerName: "",
		businessName: "",
		profession: "",
	});

	// Offer
	const [offerAmount, setOfferAmount] = useState<number | null>(null);

	const handleBasicInfoSubmit = (e: React.FormEvent) => {
		e.preventDefault();

		// Validate phone number format (international format)
		const phoneRegex = /^\+?[1-9]\d{1,14}$/;
		if (!phoneRegex.test(basicInfo.phone.replace(/[\s\-()]/g, ""))) {
			showError(
				"Please enter a valid phone number (e.g., +1234567890 or +2348012345678)",
			);
			return;
		}

		setCurrentStep("career");
	};

	const handleCareerInfoSubmit = (e: React.FormEvent) => {
		e.preventDefault();

		// Validate required fields based on employment status
		if (
			careerInfo.employmentStatus === "employed" ||
			careerInfo.employmentStatus === "self-employed"
		) {
			if (
				careerInfo.employmentStatus === "employed" &&
				!careerInfo.employerName?.trim()
			) {
				showError("Employer name is required for employed status");
				return;
			}
			if (
				careerInfo.employmentStatus === "self-employed" &&
				!careerInfo.businessName?.trim()
			) {
				showError("Business name is required for self-employed status");
				return;
			}
			if (!careerInfo.profession?.trim()) {
				showError("Profession is required");
				return;
			}
		}

		setCurrentStep("offer");
	};

	const handleOfferSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!offerAmount || offerAmount <= 0) {
			showError("Please enter a valid offer amount");
			return;
		}

		if (!property.purchase_price) {
			showError("Property purchase price is not available");
			return;
		}

		try {
			if (!property.purchase_price) {
				showError("Property purchase price is not available");
				return;
			}

			// Convert employment status from frontend format to backend format
			const employmentStatus =
				careerInfo.employmentStatus === "self-employed"
					? "self_employed"
					: careerInfo.employmentStatus;

			// Ensure empty strings are converted to undefined for optional fields
			const employerName =
				careerInfo.employmentStatus === "employed" &&
				careerInfo.employerName?.trim()
					? careerInfo.employerName.trim()
					: undefined;

			const businessName =
				careerInfo.employmentStatus === "self-employed" &&
				careerInfo.businessName?.trim()
					? careerInfo.businessName.trim()
					: undefined;

			const profession = careerInfo.profession?.trim() || undefined;

			// Check if offer matches quoted price
			const isMatchingQuote = offerAmount === Number(property.purchase_price);

			await createPropertyRequest({
				property_id: property.id,
				request_type: "buy",
				first_name: basicInfo.firstName,
				last_name: basicInfo.lastName,
				email: basicInfo.email,
				phone: basicInfo.phone,
				employment_status: employmentStatus as
					| "employed"
					| "self_employed"
					| "student"
					| "unemployed",
				employer_name: employerName,
				business_name: businessName,
				profession: profession,
				quoted_amount: Number(property.purchase_price),
				offer_amount: offerAmount ? Number(offerAmount) : undefined,
				willing_to_pay_quoted: isMatchingQuote,
			}).unwrap();

			success(
				"Buy request submitted successfully! Admin will review your offer.",
			);
			onClose();
			// Reset form
			setCurrentStep("basic");
			setBasicInfo({ firstName: "", lastName: "", email: "", phone: "" });
			setCareerInfo({
				employmentStatus: "",
				employerName: "",
				businessName: "",
				profession: "",
			});
			setOfferAmount(null);
		} catch (err: any) {
			console.error("Buy request error:", err);

			// Handle specific error cases
			let errorMessage = "Failed to submit buy request. Please try again.";

			if (err?.status === 429) {
				errorMessage = "Too many requests. Please wait a few minutes before trying again.";
			} else if (err?.status === 400) {
				errorMessage = err?.data?.detail || "Invalid request. Please check your information.";
			} else if (err?.status === 404) {
				errorMessage = "Property not found. It may have been removed.";
			} else if (err?.data?.detail) {
				errorMessage = err.data.detail;
			} else if (err?.data?.message) {
				errorMessage = err.data.message;
			} else if (err?.message) {
				errorMessage = err.message;
			}

			showError(errorMessage);
		}
	};

	if (!isOpen) return null;

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 overflow-y-auto">
			<div className="bg-card border border-border rounded-lg shadow-xl max-w-2xl w-full my-auto">
				{/* Header */}
				<div className="bg-card border-b border-border px-6 py-4 flex items-center justify-between">
					<h2 className="text-xl font-bold text-foreground">
						Request to Buy: {property.name}
					</h2>
					<button
						onClick={onClose}
						className="text-muted-foreground hover:text-foreground smooth-transition"
					>
						<Icon name="X" size={24} />
					</button>
				</div>

				{/* Content */}
				<div className="p-6">
					{/* Step Indicator */}
					<div className="mb-6 flex items-center justify-center space-x-2">
						{[
							{ label: "Basic Info", step: "basic" },
							{ label: "Career Info", step: "career" },
							{ label: "Make Offer", step: "offer" },
						].map((item, index) => (
							<>
								<div
									key={item.step}
									className={`flex items-center justify-center w-8 h-8 rounded-full ${
										currentStep === item.step
											? "bg-primary text-primary-foreground"
											: ["basic", "career", "offer"].indexOf(currentStep) >
											  index
											? "bg-success text-white"
											: "bg-muted text-muted-foreground"
									}`}
								>
									{index + 1}
								</div>
								{index < 2 && (
									<div
										className={`h-1 w-12 ${
											["basic", "career", "offer"].indexOf(currentStep) > index
												? "bg-success"
												: "bg-muted"
										}`}
									/>
								)}
							</>
						))}
					</div>

					{/* Step 1: Basic Info */}
					{currentStep === "basic" && (
						<form onSubmit={handleBasicInfoSubmit} className="space-y-4">
							<h3 className="text-lg font-semibold text-foreground mb-4">
								Basic Information
							</h3>

							<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
								<Input
									label="First Name"
									value={basicInfo.firstName}
									onChange={(e) =>
										setBasicInfo({ ...basicInfo, firstName: e.target.value })
									}
									required
								/>
								<Input
									label="Last Name"
									value={basicInfo.lastName}
									onChange={(e) =>
										setBasicInfo({ ...basicInfo, lastName: e.target.value })
									}
									required
								/>
							</div>

							<Input
								label="Email"
								type="email"
								value={basicInfo.email}
								onChange={(e) =>
									setBasicInfo({ ...basicInfo, email: e.target.value })
								}
								required
							/>

							<Input
								label="Phone Number"
								type="tel"
								value={basicInfo.phone}
								onChange={(e) =>
									setBasicInfo({ ...basicInfo, phone: e.target.value })
								}
								required
							/>

							<div className="flex justify-end space-x-3 pt-4">
								<Button type="button" variant="outline" onClick={onClose}>
									Cancel
								</Button>
								<Button type="submit" disabled={isLoading}>
									Next: Career Info
								</Button>
							</div>
						</form>
					)}

					{/* Step 2: Career Info */}
					{currentStep === "career" && (
						<form onSubmit={handleCareerInfoSubmit} className="space-y-4">
							<h3 className="text-lg font-semibold text-foreground mb-4">
								Career Information
							</h3>

							<Select
								label="Employment Status"
								value={careerInfo.employmentStatus}
								onValueChange={(value: SelectValue | SelectValue[]) => {
									const selectedValue = Array.isArray(value) ? value[0] : value;
									setCareerInfo({
										...careerInfo,
										employmentStatus: String(selectedValue) as
											| "employed"
											| "self-employed"
											| "student"
											| "unemployed"
											| "",
									});
								}}
								options={[
									{ value: "", label: "Select employment status" },
									{ value: "employed", label: "Employed" },
									{ value: "self-employed", label: "Self-employed" },
									{ value: "student", label: "Student" },
									{ value: "unemployed", label: "Unemployed" },
								]}
								required
							/>

							{(careerInfo.employmentStatus === "employed" ||
								careerInfo.employmentStatus === "self-employed") && (
								<>
									{careerInfo.employmentStatus === "employed" ? (
										<Input
											label="Employer Name"
											value={careerInfo.employerName}
											onChange={(e) =>
												setCareerInfo({
													...careerInfo,
													employerName: e.target.value,
												})
											}
											required
										/>
									) : (
										<Input
											label="Business or Company Name"
											value={careerInfo.businessName}
											onChange={(e) =>
												setCareerInfo({
													...careerInfo,
													businessName: e.target.value,
												})
											}
											required
										/>
									)}
									<Input
										label="Profession/Position Held"
										value={careerInfo.profession}
										onChange={(e) =>
											setCareerInfo({
												...careerInfo,
												profession: e.target.value,
											})
										}
										required
									/>
								</>
							)}

							<div className="flex justify-end space-x-3 pt-4">
								<Button
									type="button"
									variant="outline"
									onClick={() => setCurrentStep("basic")}
								>
									Back
								</Button>
								<Button type="submit" disabled={isLoading}>
									Next: Make Offer
								</Button>
							</div>
						</form>
					)}

					{/* Step 3: Make Offer */}
					{currentStep === "offer" && (
						<form onSubmit={handleOfferSubmit} className="space-y-4">
							<h3 className="text-lg font-semibold text-foreground mb-4">
								Make an Offer
							</h3>

							<div className="bg-muted/30 rounded-lg p-4 mb-4">
								<p className="text-sm text-muted-foreground mb-2">
									Quoted Sale Price:
								</p>
								<p className="text-2xl font-bold text-foreground">
									₦{property.purchase_price?.toLocaleString()}
								</p>
							</div>

							<Input
								label="Your Offer Amount (₦)"
								type="number"
								value={offerAmount || ""}
								onChange={(e) =>
									setOfferAmount(parseFloat(e.target.value) || null)
								}
								required
								min={1}
							/>

							{/* Show feedback based on offer vs quoted price */}
							{offerAmount && property.purchase_price && (
								<div
									className={`rounded-lg p-4 ${
										offerAmount === Number(property.purchase_price)
											? "bg-success/10 border border-success/20"
											: offerAmount < Number(property.purchase_price)
											? "bg-warning/10 border border-warning/20"
											: "bg-info/10 border border-info/20"
									}`}
								>
									{offerAmount === Number(property.purchase_price) ? (
										<p className="text-sm text-success font-medium">
											✓ Your offer matches the quoted sale price. This improves
											approval chances.
										</p>
									) : offerAmount < Number(property.purchase_price) ? (
										<p className="text-sm text-warning font-medium">
											⚠ Your offer is lower than the quoted price (₦
											{Number(property.purchase_price).toLocaleString()}). The
											seller may negotiate or decline.
										</p>
									) : (
										<p className="text-sm text-primary font-medium">
											Your offer is above the quoted price. The seller will
											review your offer.
										</p>
									)}
								</div>
							)}

							<div className="bg-info/10 border border-info/20 rounded-lg p-4">
								<p className="text-sm text-muted-foreground">
									Your offer will be logged and the admin will be notified. Once
									your offer is accepted, you'll be notified via email and can
									request a physical meeting for document verification.
								</p>
							</div>

							<div className="flex justify-end space-x-3 pt-4">
								<Button
									type="button"
									variant="outline"
									onClick={() => setCurrentStep("career")}
								>
									Back
								</Button>
								<Button
									type="submit"
									disabled={!offerAmount || offerAmount <= 0 || isLoading}
								>
									{isLoading ? "Submitting..." : "Submit Offer"}
								</Button>
							</div>
						</form>
					)}
				</div>
			</div>
		</div>
	);
};

export default RequestToBuyModal;
